import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Dialog } from '@/components/ui/dialog';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FileInput } from '@/components/ui/file';
import LoadingCircle from '@/components/LoadingCircle';
import {
  useGenerateCertificate,
  useUpdateCertificateStatus,
} from '@/hooks/useGiftsRequests';
import {
  CertificateStatus,
  ICertificateIdsAndStatuses,
  ICertificateParams,
} from '@/api/gifts-requests';
import axios from 'axios';
import { toast } from 'react-toastify';

// Schema cho form gửi chứng từ
const certificateFormSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'Vui lòng chọn ít nhất 1 file chứng từ')
    .max(10, 'Chỉ được tải lên tối đa 10 file'),
});

type CertificateFormValues = z.infer<typeof certificateFormSchema>;

const Certificate = ({ id }: { id: string }) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: generateCertificate } = useGenerateCertificate();
  const { mutateAsync: updateCertificateStatus } = useUpdateCertificateStatus();

  const form = useForm<CertificateFormValues>({
    resolver: zodResolver(certificateFormSchema),
    defaultValues: {
      files: [],
    },
    mode: 'onChange',
  });

  const onSubmit = async (values: CertificateFormValues) => {
    setIsSubmitting(true);
    try {
      const payload: ICertificateParams = {
        fileNames: values.files.map((file) => ({
          name: file.name,
          type: file.type,
        })),
      };
      const response = await generateCertificate({
        id,
        payload,
      });

      // Track upload results for each file
      interface UploadResult {
        url: string;
        status: CertificateStatus;
        success: boolean;
        fileName: string;
      }

      const uploadResults: UploadResult[] = [];

      // Create upload promises for parallel execution
      const uploadPromises = response.map(async (item, index) => {
        try {
          const uploadResponse = await axios.put(
            item.uploadUrl,
            values.files[index],
            {
              headers: { 'Content-Type': values.files[index].type },
            }
          );

          // S3 returns status 200 for successful uploads
          if (uploadResponse.status === 200) {
            return {
              url: item.url,
              status: CertificateStatus.DONE,
              success: true,
              fileName: values.files[index].name,
            };
          } else {
            return {
              url: item.url,
              status: CertificateStatus.FAILED,
              success: false,
              fileName: values.files[index].name,
            };
          }
        } catch {
          return {
            url: item.url,
            status: CertificateStatus.FAILED,
            success: false,
            fileName: values.files[index].name,
          };
        }
      });

      // Execute all uploads in parallel and wait for all to complete
      const uploadResults_raw = await Promise.allSettled(uploadPromises);

      // Process results from Promise.allSettled
      uploadResults_raw.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          uploadResults.push(result.value);
        } else {
          uploadResults.push({
            url: response[index].url,
            status: CertificateStatus.FAILED,
            success: false,
            fileName: values.files[index].name,
          });
        }
      });

      // Prepare payload for status update
      const payloadDone: ICertificateIdsAndStatuses[] = uploadResults.map(
        (result) => ({
          url: result.url,
          status: result.status,
        })
      );

      await updateCertificateStatus(payloadDone);
      setOpen(false);
      form.reset();
    } catch {
      toast.error('Có lỗi xảy ra khi tải lên chứng từ. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="luxury-button-outline"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Send />
          Gửi chứng từ
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">Gửi chứng từ</DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">Gửi chứng từ</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* File Upload Section */}
            <FormField
              control={form.control}
              name="files"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="luxury-label">
                    Tải lên chứng từ <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <FileInput
                      multiple
                      accept=".pdf, .jpg, .jpeg, .png"
                      value={field.value}
                      onChange={field.onChange}
                      maxFiles={10}
                      maxSize={10 * 1024 * 1024} // 10MB
                      placeholder="Kéo thả tập tin hoặc nhấn để chọn"
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Section */}
            <div className="flex justify-center gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                }}
                disabled={isSubmitting}
                className="luxury-button-outline"
              >
                Hủy
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="luxury-button-primary"
              >
                {isSubmitting ? (
                  <>
                    <LoadingCircle />
                    Đang gửi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Gửi chứng từ
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default Certificate;
